import { useState } from 'react';
import { formatDate } from '../lib/format.js';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Stack,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Switch,
  Alert,
  Snackbar,
  Tooltip,
} from '@mui/material';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import VerifiedIcon from '@mui/icons-material/Verified';
import { useMe } from '../api/hooks.js';
import { api } from '../api/client.js';
import { useT } from '../i18n/useT.js';
import { useSort } from '../lib/useSort.js';

interface AdminUser {
  id: string;
  email: string;
  fullName: string | null;
  emailVerified: boolean;
  status: 'ACTIVE' | 'DISABLED' | 'INVITED';
  isSuperAdmin: boolean;
  /** Super-admin comes from the SUPER_ADMIN_EMAILS allowlist → not togglable here. */
  superAdminLocked?: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  organizations: { id: string; name: string; role: string }[];
}

const fmtDate = (d: string | null) => (d ? formatDate(d) : '—');

export function AdminUsersPage() {
  const all = useT();
  const t = all.admin.users;
  const needSuperAdmin = all.admin.needSuperAdmin;
  const { data: me } = useMe();
  const qc = useQueryClient();
  const [toast, setToast] = useState<string | null>(null);

  const users = useQuery({
    enabled: !!me?.isSuperAdmin,
    queryKey: ['admin-users'],
    queryFn: async () => (await api.get('/v1/admin/users')).data.data as AdminUser[],
  });

  const sort = useSort<'user' | 'companies' | 'lastLogin' | 'active' | 'superAdmin'>('lastLogin');
  const rows = sort.sorted(users.data ?? [], {
    user: (u) => u.fullName || u.email,
    companies: (u) => u.organizations.length,
    lastLogin: (u) => u.lastLoginAt,
    active: (u) => (u.status === 'ACTIVE' ? 1 : 0),
    superAdmin: (u) => (u.isSuperAdmin ? 1 : 0),
  });

  const patchUser = useMutation({
    mutationFn: async (v: { id: string; status?: 'ACTIVE' | 'DISABLED'; isSuperAdmin?: boolean }) =>
      api.patch(`/v1/admin/users/${v.id}`, { status: v.status, isSuperAdmin: v.isSuperAdmin }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      setToast(t.updated);
    },
    onError: (e: unknown) => {
      const msg =
        (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ??
        t.updateFailed;
      setToast(msg);
    },
  });

  if (me && !me.isSuperAdmin) {
    return <Alert severity="error">{needSuperAdmin}</Alert>;
  }

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <PeopleAltIcon color="primary" />
        <Typography variant="h5">{t.title}</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t.subtitle}
      </Typography>

      <Paper sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t.columns.user}</TableCell>
              <TableCell>{t.columns.companies}</TableCell>
              <TableCell>{t.columns.lastLogin}</TableCell>
              <TableCell align="center">{t.columns.active}</TableCell>
              <TableCell align="center">{t.columns.superAdmin}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((u) => {
              const isSelf = u.id === me?.id;
              return (
                <TableRow key={u.id} hover>
                  <TableCell>
                    <Stack>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="body2" fontWeight={600}>
                          {u.fullName || u.email}
                        </Typography>
                        {u.emailVerified ? (
                          <Tooltip title={t.verified}>
                            <VerifiedIcon sx={{ fontSize: 15, color: 'success.main' }} />
                          </Tooltip>
                        ) : (
                          <Tooltip title={t.notVerified}>
                            <Chip size="small" label="unverified" color="warning" variant="outlined" sx={{ height: 18, fontSize: 10 }} />
                          </Tooltip>
                        )}
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {u.email}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                      {u.organizations.length === 0 && (
                        <Typography variant="caption" color="text.secondary">
                          —
                        </Typography>
                      )}
                      {u.organizations.map((o) => (
                        <Chip
                          key={o.id}
                          size="small"
                          variant="outlined"
                          label={`${o.name} · ${o.role.toLowerCase()}`}
                          sx={{ height: 20, fontSize: 11 }}
                        />
                      ))}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {fmtDate(u.lastLoginAt)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={isSelf ? t.selfDisable : u.status === 'ACTIVE' ? t.disable : t.enable}>
                      <span>
                        <Switch
                          checked={u.status === 'ACTIVE'}
                          disabled={isSelf || patchUser.isPending}
                          onChange={(e) =>
                            patchUser.mutate({ id: u.id, status: e.target.checked ? 'ACTIVE' : 'DISABLED' })
                          }
                        />
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip
                      title={
                        u.superAdminLocked
                          ? t.allowlisted
                          : isSelf
                            ? "You can't revoke your own super-admin access"
                            : t.toggleSuperAdmin
                      }
                    >
                      <span>
                        <Switch
                          color="secondary"
                          checked={u.isSuperAdmin}
                          disabled={u.superAdminLocked || (isSelf && u.isSuperAdmin) || patchUser.isPending}
                          onChange={(e) => patchUser.mutate({ id: u.id, isSuperAdmin: e.target.checked })}
                        />
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
            {users.data && users.data.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No users yet.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Snackbar
        open={!!toast}
        autoHideDuration={2500}
        onClose={() => setToast(null)}
        message={toast ?? ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}
